import React, { useState } from 'react';
import { 
  FileText, File, Image, Video, Music, 
  FileJson, FileCode, Archive, Table, 
  Presentation, Loader2
} from 'lucide-react';
import { Tooltip } from '@/components/ui';

interface AttachmentItemProps {
  fileName: string;
  fileSize: string;
  contentType?: string;
  public_url?: string;
  onOpen?: () => void;
  onDownload?: (progress: (progress: number) => void) => Promise<void>;
}

// Function to truncate filename while preserving extension
const truncateFilename = (filename: string, maxLength: number = 30) => {
  if (filename.length <= maxLength) return filename;

  const lastDotIndex = filename.lastIndexOf('.');
  if (lastDotIndex === -1) {
    // No extension
    return `${filename.slice(0, maxLength - 3)}...`;
  }

  const extension = filename.slice(lastDotIndex);
  const nameWithoutExt = filename.slice(0, lastDotIndex);
  const maxNameLength = maxLength - extension.length - 3; // 3 for the ellipsis

  if (maxNameLength < 5) {
    // If the name would be too short, just truncate from the end
    return `${filename.slice(0, maxLength - 3)}...`;
  }

  const start = nameWithoutExt.slice(0, Math.ceil(maxNameLength / 2));
  const end = nameWithoutExt.slice(-(Math.floor(maxNameLength / 2)));
  
  return `${start}...${end}${extension}`;
};

const getFileIcon = (contentType?: string) => {
  if (!contentType) return <File size={24} />; // Return default icon if no contentType
  
  const type = contentType.toLowerCase();
  
  if (type.includes('image/')) {
    return <Image size={24} />;
  }
  if (type.includes('video/')) {
    return <Video size={24} />;
  }
  if (type.includes('audio/')) {
    return <Music size={24} />;
  }
  if (type === 'application/pdf') {
    return <FileText size={24} />;
  }
  if (type.includes('text/') || type.includes('application/json') || 
      type.includes('application/xml') || type.includes('application/javascript')) {
    return <FileCode size={24} />;
  }
  if (type.includes('spreadsheet') || type.includes('excel') || type === 'application/vnd.ms-excel') {
    return <Table size={24} />;
  }
  if (type.includes('presentation') || type.includes('powerpoint')) {
    return <Presentation size={24} />;
  }
  if (type.includes('zip') || type.includes('rar') || type.includes('7z') || 
      type.includes('tar') || type.includes('gzip')) {
    return <Archive size={24} />;
  }
  if (type.includes('text/plain')) {
    return <FileText size={24} />;
  }
  if (type.includes('application/json')) {
    return <FileJson size={24} />;
  }

  return <File size={24} />;
};

const getFileColor = (contentType?: string) => {
  if (!contentType) return '#64748b'; // Return default color if no contentType
  
  const type = contentType.toLowerCase();
  
  if (type.includes('image/')) {
    return '#22c55e'; // Green
  }
  if (type.includes('video/')) {
    return '#ef4444'; // Red
  }
  if (type.includes('audio/')) {
    return '#8b5cf6'; // Purple
  }
  if (type === 'application/pdf') {
    return '#f97316'; // Orange
  }
  if (type.includes('text/') || type.includes('application/json') || 
      type.includes('application/xml') || type.includes('application/javascript')) {
    return '#3b82f6'; // Blue
  }
  if (type.includes('spreadsheet') || type.includes('excel')) {
    return '#059669'; // Emerald
  }
  if (type.includes('presentation') || type.includes('powerpoint')) {
    return '#ea580c'; // Orange
  }
  if (type.includes('zip') || type.includes('rar') || type.includes('7z')) {
    return '#7c3aed'; // Violet
  }
  return '#64748b'; // Gray
};

const AttachmentItem: React.FC<AttachmentItemProps> = ({
  fileName,
  fileSize,
  contentType,
  public_url,
  onOpen,
  onDownload
}) => {
  const icon = getFileIcon(contentType);
  const color = getFileColor(contentType);
  const [downloadProgress, setDownloadProgress] = useState<number | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  const handleClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (onOpen) {
      onOpen();
      return;
    }

    if (!public_url && onDownload) {
      if (isDownloading) return;

      try {
        setIsDownloading(true);
        setDownloadProgress(0);
        await onDownload((progress) => {
          setDownloadProgress(progress);
        });
      } finally {
        setIsDownloading(false);
        setDownloadProgress(null);
      }
    }
  };

  const displayName = truncateFilename(fileName);

  return (
    <Tooltip text={displayName} position="top">
      <a
        href={public_url || '#'}
        download={onOpen ? undefined : fileName}
        rel="noopener noreferrer"
        onClick={handleClick}
        role="button"
        className="flex items-center justify-between p-3 rounded-lg border border-gray-200 bg-white hover:shadow-sm transition-all duration-200 cursor-pointer"
        style={{
          width: '100%',
          textDecoration: 'none',
          color: 'inherit',
          position: 'relative'
        }}
      >
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div 
            className="flex items-center justify-center w-10 h-10 rounded-lg flex-shrink-0"
            style={{ 
              backgroundColor: `${color}15`,
              color: color
            }}
          >
            {isDownloading ? (
              <Loader2 className="w-6 h-6 animate-spin" />
            ) : (
              icon
            )}
          </div>
          
          <div className="flex flex-col min-w-0 flex-1">
            <span 
              className="text-sm font-medium text-gray-900" 
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                width: '100%'
              }}
            >
              {displayName}
            </span>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">
                {isDownloading && downloadProgress !== null 
                  ? `Downloading... ${Math.round(downloadProgress)}%`
                  : fileSize
                }
              </span>
            </div>
          </div>
        </div>

        {/* Download Progress Bar */}
        {isDownloading && downloadProgress !== null && (
          <div 
            style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              width: `${downloadProgress}%`,
              height: '2px',
              backgroundColor: color,
              transition: 'width 0.2s ease-in-out',
              borderRadius: '0 0 0 8px'
            }}
          />
        )}
      </a>
    </Tooltip>
  );
};

export default AttachmentItem;
